// Terminal-state reconciliation for the Deployments UI (issue #134).
//
// The raw `Deployment` records served by central-server can stay
// `in_progress` forever: a device that was offline at deploy time, or that
// never reported a terminal status, leaves the deployment's progress counters
// permanently short of 100%. The operator is then staring at a spinner that
// will never resolve.
//
// Rather than wait for a backend migration, we reconcile each deployment
// against the AUTHORITATIVE per-device state (the device's actual reported
// `softwareVersion` vs the release version it was asked to install, plus
// whether the device is currently online). This is the same "derive status
// from reality, not from drifting bookkeeping" approach the issue asks for —
// it makes the deployments view deterministic and lets every device reach an
// explicit terminal label (`up_to_date` / `failed` / `timed_out` / `offline`)
// instead of hanging in `in_progress`.
//
// This module is pure (no I/O) so the logic is easy to reason about and reuse.

import type { CentralEdgeDevice } from '@simcheck/shared-types';
import type { Deployment, DeviceDeploymentStatus } from './deploymentService';

// A device whose update hasn't completed and which has been offline / silent
// past this window is treated as terminally stalled for that deployment,
// rather than perpetually "in progress".
export const OFFLINE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export type EffectiveDeviceState =
  | 'up_to_date'   // device reports the deployment's target version
  | 'superseded'   // device reports a version NEWER than the target — this
                   // deployment was satisfied (or skipped) and then replaced
                   // by a later one; it must not read as failed/timed-out
  | 'updating'     // device online, not yet on target, within timeout
  | 'failed'       // device reported a hard failure for this deployment
  | 'timed_out'    // online but never reached target before the timeout
  | 'offline';     // device offline / never reported, can't make progress

export interface ReconciledDevice {
  deviceId: string;
  name: string;
  reportedVersion?: string;
  rawStatus?: DeviceDeploymentStatus['status'];
  effective: EffectiveDeviceState;
  online: boolean;
  installPhase?: string;
  error?: string;
}

export interface ReconciledDeployment {
  deployment: Deployment;
  devices: ReconciledDevice[];
  counts: Record<EffectiveDeviceState, number>;
  total: number;
  // True once every targeted device has reached an explicit terminal state
  // (no device is still legitimately `updating`). A settled deployment is
  // never shown as an open-ended spinner.
  settled: boolean;
  // Derived headline status the UI renders instead of the raw, possibly-stuck
  // `deployment.status`. `superseded` = every targeted device is at or beyond
  // the target via a LATER deployment — historically done, not failed.
  effectiveStatus: 'completed' | 'failed' | 'in_progress' | 'stalled' | 'superseded';
}

function emptyCounts(): Record<EffectiveDeviceState, number> {
  return { up_to_date: 0, superseded: 0, updating: 0, failed: 0, timed_out: 0, offline: 0 };
}

/**
 * Semver-ish compare (mirrors central-server's cmpSemver): numeric segment
 * compare so "4.10.0" > "4.9.1". Non-numeric / missing segments compare as 0.
 * Returns <0 | 0 | >0.
 */
export function cmpSemver(a: string, b: string): number {
  const pa = a.split('.').map((s) => parseInt(s, 10) || 0);
  const pb = b.split('.').map((s) => parseInt(s, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

function lastSeenMs(device?: CentralEdgeDevice): number | null {
  if (!device?.lastHeartbeat) return null;
  const d = device.lastHeartbeat instanceof Date
    ? device.lastHeartbeat
    : new Date(device.lastHeartbeat as unknown as string);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function isOnline(device: CentralEdgeDevice | undefined, now: number): boolean {
  if (!device) return false;
  if (device.status === 'online' || device.status === 'connecting') {
    // Cross-check the heartbeat — a stale `status` field shouldn't keep a
    // dead device "online".
    const seen = lastSeenMs(device);
    if (seen == null) return device.status === 'online';
    return now - seen <= OFFLINE_TIMEOUT_MS;
  }
  return false;
}

export function deploymentCreatedMs(deployment: Deployment): number {
  const c: any = deployment.createdAt;
  if (!c) return 0;
  if (typeof c._seconds === 'number') return c._seconds * 1000;
  if (typeof c === 'string') {
    const t = new Date(c).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof c.seconds === 'number') return c.seconds * 1000;
  return 0;
}

/**
 * Is this a device a release can actually be deployed to?
 *
 * Synthetic demo devices (`syntheticDemo: true` — the AutoConnect Global
 * showcase fleet) are Firestore furniture with no Pi behind them. They carry
 * `isDemo: false` + `registrationStatus: 'approved'` and a cron re-stamps their
 * heartbeat every minute, so the plain `!isDemo && approved` test reads them as
 * a live fleet. Nothing ever polls desired-state for them, so a pin can never
 * converge — they'd sit "updating" forever and bury the real fleet's progress.
 *
 * central-server refuses to target them; this mirrors that rule client-side so
 * the Fleet view, the deploy wizard, and deployments created BEFORE the
 * server-side guard landed all agree on the real device universe.
 */
export function isDeployableDevice(device: {
  isDemo?: boolean;
  registrationStatus?: string;
  syntheticDemo?: boolean;
}): boolean {
  if ((device as { syntheticDemo?: boolean }).syntheticDemo === true) return false;
  return !device.isDemo && device.registrationStatus === 'approved';
}

/**
 * Resolve the targeted device id set for a deployment. Prefer the per-device
 * status keys the backend recorded (authoritative set of what was actually
 * notified); fall back to the explicit `targets.deviceIds`; finally, for a
 * "deploy to all" record with neither, fall back to every approved real device
 * — so the page can never silently omit a targeted device.
 *
 * Whatever the source, ids that resolve to a known non-deployable device are
 * dropped: a historical record that captured the synthetic fleet must not keep
 * reporting phantom targets forever.
 */
export function resolveTargetedDeviceIds(
  deployment: Deployment,
  approvedDevices: CentralEdgeDevice[],
  devicesById?: Map<string, CentralEdgeDevice>,
): string[] {
  // Only drop ids we can positively identify as non-deployable — an id with no
  // matching device doc stays in, so a genuinely deleted/unknown target is
  // still surfaced rather than silently vanishing.
  const keep = (ids: string[]) => (
    devicesById
      ? ids.filter((id) => {
          const d = devicesById.get(id);
          return !d || isDeployableDevice(d);
        })
      : ids
  );

  const fromStatuses = Object.keys(deployment.deviceStatuses || {});
  if (fromStatuses.length > 0) return keep(fromStatuses);
  if (deployment.targets?.deviceIds && deployment.targets.deviceIds.length > 0) {
    return keep(deployment.targets.deviceIds);
  }
  if (deployment.targets?.all) {
    return approvedDevices.map((d) => d.deviceId || d.id);
  }
  return [];
}

/**
 * Reconcile one deployment against the live device registry.
 */
export function reconcileDeployment(
  deployment: Deployment,
  devicesById: Map<string, CentralEdgeDevice>,
  approvedDevices: CentralEdgeDevice[],
  now: number = Date.now(),
): ReconciledDeployment {
  const targetVersion = deployment.releaseVersion;
  const targetedIds = resolveTargetedDeviceIds(deployment, approvedDevices, devicesById);
  const createdMs = deploymentCreatedMs(deployment);
  const counts = emptyCounts();

  const devices: ReconciledDevice[] = targetedIds.map((deviceId) => {
    const device = devicesById.get(deviceId);
    const raw = deployment.deviceStatuses?.[deviceId];
    const online = isOnline(device, now);
    const reportedVersion = device?.softwareVersion;

    let effective: EffectiveDeviceState;
    if (reportedVersion && targetVersion && reportedVersion === targetVersion) {
      // The single source of truth: the device is actually running the target.
      effective = 'up_to_date';
    } else if (reportedVersion && targetVersion && cmpSemver(reportedVersion, targetVersion) > 0) {
      // The device has moved BEYOND this deployment's target via a later
      // deployment. Historically satisfied — without this branch every old
      // deployment reads as timed_out/failed once the fleet advances.
      effective = 'superseded';
    } else if (raw?.status === 'failed' || device?.updateStatus === 'update_failed') {
      effective = 'failed';
    } else if (!online) {
      effective = 'offline';
    } else if (createdMs === 0 || now - createdMs > OFFLINE_TIMEOUT_MS) {
      // Online, past the timeout, still not on the target version. A
      // deployment with no resolvable createdAt is treated as past the
      // timeout (fail closed) — it must still reach a terminal state rather
      // than spin as `updating` forever.
      effective = 'timed_out';
    } else {
      effective = 'updating';
    }

    counts[effective] += 1;
    return {
      deviceId,
      name: device?.name || deviceId,
      reportedVersion,
      rawStatus: raw?.status,
      effective,
      online,
      installPhase: device?.installPhase,
      error: raw?.error,
    };
  });

  const total = devices.length;
  const settled = counts.updating === 0;
  // Devices at the target OR beyond it (via a later deployment) both satisfy
  // this deployment for headline purposes.
  const satisfied = counts.up_to_date + counts.superseded;

  let effectiveStatus: ReconciledDeployment['effectiveStatus'];
  if (total > 0 && satisfied === total) {
    effectiveStatus = counts.up_to_date === 0 ? 'superseded' : 'completed';
  } else if (!settled) {
    effectiveStatus = 'in_progress';
  } else if (counts.failed > 0 || counts.timed_out > 0 || counts.offline > 0) {
    // Everything has stopped moving but not everything reached the target.
    effectiveStatus = satisfied === 0 ? 'failed' : 'stalled';
  } else {
    effectiveStatus = 'completed';
  }

  return { deployment, devices, counts, total, settled, effectiveStatus };
}

// ---------------------------------------------------------------------------
// Fleet view: one row per device, current vs desired version + update state.
// ---------------------------------------------------------------------------

export type FleetUpdateState =
  | 'up_to_date'   // softwareVersion === desiredVersion (or no desired pin)
  | 'updating'     // online, behind desired, install in flight / pending
  | 'stuck'        // online, behind desired past the timeout — not converging
  | 'update_failed'
  | 'offline';     // can't converge — offline

export interface FleetRow {
  device: CentralEdgeDevice;
  deviceId: string;
  name: string;
  locationName?: string;
  currentVersion: string;
  desiredVersion?: string;
  online: boolean;
  state: FleetUpdateState;
  installPhase?: string;
  lastSeenMs: number | null;
}

export function buildFleetRow(
  device: CentralEdgeDevice,
  now: number = Date.now(),
  opts?: {
    // When the device's current `desiredVersion` pin was written (newest
    // matching deployment's createdAt) — lets a row whose install never even
    // started still time out instead of "updating" forever.
    pinnedSinceMs?: number | null;
  },
): FleetRow {
  const online = isOnline(device, now);
  const currentVersion = device.softwareVersion || '—';
  const desiredVersion = device.desiredVersion;
  // At-or-above the pin counts as current: a stale lower pin (e.g. desired
  // 4.6.11 left behind after the fleet moved to 4.7.1) must not render a
  // healthy device as updating/stuck. Mirrors the deployment reconciler's
  // superseded handling.
  const onDesired = !desiredVersion
    || (!!device.softwareVersion && cmpSemver(device.softwareVersion, desiredVersion) >= 0);

  let state: FleetUpdateState;
  if (onDesired) {
    // A device actually running its desired version is current, even if a
    // stale `update_failed` flag from a prior attempt is still set.
    state = 'up_to_date';
  } else if (device.updateStatus === 'update_failed') {
    state = 'update_failed';
  } else if (!online) {
    state = 'offline';
  } else {
    // Online + behind desired. Bound the `updating` state so a device that
    // never converges can't spin (and pin the auto-refresh poll) forever:
    // prefer the updater's own install-start timestamp; fall back to when
    // the pin was written. No evidence at all → stay `updating` (fail open —
    // a fresh pin the updater hasn't polled yet looks exactly like this).
    const sinceMs =
      (typeof device.installPhaseStartedAt === 'number' && device.installPhaseStartedAt > 0)
        ? device.installPhaseStartedAt
        : (opts?.pinnedSinceMs ?? null);
    state = sinceMs != null && now - sinceMs > OFFLINE_TIMEOUT_MS ? 'stuck' : 'updating';
  }

  return {
    device,
    deviceId: device.deviceId || device.id,
    name: device.name || device.deviceId || device.id,
    locationName: device.locationName,
    currentVersion,
    desiredVersion,
    online,
    state,
    installPhase: device.installPhase,
    lastSeenMs: lastSeenMs(device),
  };
}
